import { Link, useNavigate } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { notificationsPagePath } from "../lib/notificationsRoutes";
import { dismissNotificationIfViewed } from "../lib/notificationDismiss";
import { normalizeNotificationActionHref } from "../lib/rejectionNotification";
import { notificationBellOpenPath } from "../lib/notificationNavigation";
import { formatNotificationRelativeTime, getNotificationVisualKind } from "../lib/notificationListDisplay";
import NotificationListRowVisual from "./NotificationListRowVisual";
import { parseSafeInternalNextPath } from "../navigateAfterAuth";

/**
 * @param {{
 *   open: boolean,
 *   role?: string,
 *   accent: "p1" | "p2",
 *   loading: boolean,
 *   notifications: Array<{ id: string, title: string, body?: string, actionHref: string, isActive?: boolean, createdAt?: string, type?: string }>,
 *   authToken?: string,
 *   onSelectNotification: () => void,
 *   onClose: () => void,
 * }} props
 */
export default function NotificationDropdown({
  open,
  role,
  accent,
  loading,
  notifications,
  authToken,
  onSelectNotification,
  onClose,
}) {
  const navigate = useNavigate();
  const notificationsPath = notificationsPagePath(role);
  const items = Array.isArray(notifications) ? notifications.slice(0, 10) : [];

  if (!open) return null;

  const handleRowClick = async (notification) => {
    const path =
      parseSafeInternalNextPath(notificationBellOpenPath(notification, role)) ||
      parseSafeInternalNextPath(
        normalizeNotificationActionHref(notification?.actionHref, role)
      ) ||
      "";
    if (!path || !path.startsWith("/")) return;
    await dismissNotificationIfViewed(authToken, role, notification);
    onSelectNotification();
    onClose();
    navigate(path);
  };

  return (
    <div
      className={`jw-notification-dropdown jw-notification-dropdown--${accent}`}
      role="dialog"
      aria-labelledby="jw-notification-dropdown-title"
      aria-modal="false"
    >
      <div className="jw-notification-dropdown__header">
        <div className="jw-notification-dropdown__header-text">
          <h2 id="jw-notification-dropdown-title" className="jw-notification-dropdown__title">
            Notifications
          </h2>
          {!loading && items.length > 0 ? (
            <p className="jw-notification-dropdown__subtitle">
              {items.length === 1 ? "1 new update" : `${items.length} new updates`}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className="jw-notification-dropdown__close"
          onClick={onClose}
          aria-label="Close notifications"
        >
          <X className="jw-notification-dropdown__close-icon" aria-hidden />
        </button>
      </div>

      <div className="jw-notification-dropdown__body">
        {loading ? (
          <p className="jw-notification-dropdown__status">Loading…</p>
        ) : items.length > 0 ? (
          <ul className="jw-notification-dropdown__list">
            {items.map((n) => {
              const kind = getNotificationVisualKind(n);
              const relativeTime = formatNotificationRelativeTime(n.createdAt);

              return (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`jw-notification-item jw-notification-item--dropdown jw-notification-item--active-${accent} jw-notification-item--kind-${kind}`}
                    onClick={() => void handleRowClick(n)}
                  >
                    <NotificationListRowVisual
                      notification={n}
                      accent={accent}
                      showChevron
                      showRelativeTime
                      relativeTime={relativeTime}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="jw-notification-dropdown__empty">
            <span className="jw-notification-dropdown__empty-icon" aria-hidden>
              <Bell size={22} strokeWidth={1.75} />
            </span>
            <p className="jw-notification-dropdown__empty-text">You&apos;re all caught up — nothing new right now.</p>
          </div>
        )}
      </div>

      <div className="jw-notification-dropdown__footer">
        <Link to={notificationsPath} className="jw-notification-dropdown__see-all" onClick={onClose}>
          See all notifications
        </Link>
      </div>
    </div>
  );
}
